import { useId } from "react";

import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";

import "@/app/features/diagrams/functions/s1000d/geometryTopologySpec";

export function GeometryBindingGapVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={720}
      height={485}
      title="The S1000D Identifier-to-Geometry Binding Gap"
      description="Architectural comparison illustrating the native S1000D specification domain (DMC hierarchy, IPC parts, 2D vector hotspots) versus the external CAD 3D domain, highlighting the out-of-band binding matrix that requires continuous maintenance."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 720 485"
      frame={{
        kind: "boundary",
        label: "THE IDENTIFIER-TO-GEOMETRY BINDING SEAM",
        x: 16,
        y: 16,
        width: 688,
        height: 452,
      }}
    >
      <DiagramMarkers prefix="geom-" />

      {/* LEFT COLUMN: S1000D STANDARD DOMAIN */}
      <rect
        x="35"
        y="42"
        width="230"
        height="338"
        rx="8"
        fill="none"
        stroke="var(--ga-brass, #9c6b3e)"
        strokeWidth="1.2"
      />
      <text
        x="48"
        y="64"
        fill="var(--ga-brass, #9c6b3e)"
        className="text-[11px] font-mono tracking-wider font-bold uppercase"
      >
        S1000D Domain (Native)
      </text>
      <text
        x="48"
        y="78"
        fill="currentColor"
        className="text-[10px] opacity-70 font-mono"
      >
        Zero maintenance tax · In-spec
      </text>

      <DiagramNode
        x={45}
        y={90}
        width={210}
        height={70}
        eyebrow="SYSTEM STRUCTURE"
        label="DMC & SNS Hierarchy"
        sublabel="Functional SNS Tree"
        variant="brass"
      />

      <DiagramNode
        x={45}
        y={170}
        width={210}
        height={70}
        eyebrow="PARTS CATALOG"
        label="IPC Line Items"
        sublabel="Part Numbers & Masks"
        variant="standard"
      />

      <DiagramNode
        x={45}
        y={250}
        width={210}
        height={70}
        eyebrow="2D HOTSPOT BINDING"
        label="WebCGM & SVG Hotspots"
        sublabel="2D In-Corpus Vectors"
        variant="standard"
      />

      <text
        x="50"
        y="360"
        fill="var(--ga-diagram-success, #15803d)"
        className="text-[10px] font-mono font-semibold"
      >
        ✓ Shipped inside the corpus
      </text>

      {/* RIGHT COLUMN: CAD / 3D DOMAIN */}
      <rect
        x="455"
        y="42"
        width="230"
        height="338"
        rx="8"
        fill="none"
        stroke="var(--ga-diagram-agent, #2563eb)"
        strokeWidth="1.2"
        strokeDasharray="5 3"
      />
      <text
        x="468"
        y="64"
        fill="var(--ga-diagram-agent, #2563eb)"
        className="text-[11px] font-mono tracking-wider font-bold uppercase"
      >
        3D CAD Domain (External)
      </text>
      <text
        x="468"
        y="78"
        fill="currentColor"
        className="text-[10px] opacity-70 font-mono"
      >
        Engineering CAD / glTF / STEP
      </text>

      <DiagramNode
        x={465}
        y={90}
        width={210}
        height={70}
        eyebrow="CAD ASSEMBLY"
        label="Assembly Hierarchy"
        sublabel="STEP & CAD Assemblies"
        variant="standard"
      />

      <DiagramNode
        x={465}
        y={170}
        width={210}
        height={70}
        eyebrow="POLYGON MESH"
        label="Spatial Geometry"
        sublabel="Mesh Nodes & Buffers"
        variant="standard"
      />

      <DiagramNode
        x={465}
        y={250}
        width={210}
        height={70}
        eyebrow="TRANSFORMS"
        label="Coordinate Frames"
        sublabel="Transform Coordinates"
        variant="standard"
      />

      <text
        x="470"
        y="360"
        fill="var(--ga-diagram-danger, #dc2626)"
        className="text-[10px] font-mono font-semibold"
      >
        ✕ Carries zero S1000D semantics
      </text>

      {/* CENTER: THE BINDING MATRIX (THE SEAM) */}
      <path
        d="M 255 205 L 285 205"
        fill="none"
        stroke="var(--ga-brass, #9c6b3e)"
        strokeWidth="1.5"
        markerEnd="url(#geom-arrow-neutral)"
      />
      <path
        d="M 465 205 L 435 205"
        fill="none"
        stroke="var(--ga-diagram-agent, #2563eb)"
        strokeWidth="1.5"
        markerEnd="url(#geom-arrow-blue)"
      />

      <DiagramNode
        x={285}
        y={170}
        width={150}
        height={70}
        eyebrow="OUT-OF-BAND SEAM"
        label="Binding Matrix"
        sublabel="Part # ⟷ Mesh UUID"
        variant="repair"
      />

      <path
        d="M 360 240 L 360 250"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        markerEnd="url(#geom-arrow-neutral)"
      />

      {/* DRIFT DETECTOR / VALIDATOR */}
      <DiagramNode
        x={275}
        y={250}
        width={170}
        height={70}
        eyebrow="PERPETUAL TAX"
        label="Drift Validator"
        sublabel="Monitors ECO Drift"
        variant="fallback"
      />

      {/* BOTTOM SUMMARY CALLOUT */}
      <DiagramNode
        x={80}
        y={398}
        width={560}
        height={54}
        eyebrow="ARCHITECTURAL REALITY"
        label="3D Requires an Out-of-Band Binding Layer"
        sublabel="Identifiers live in S1000D; spatial CAD geometry must be mapped externally"
        variant="standard"
      />
    </DiagramFrame>
  );
}
